import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridCellProps,
  GridColumnMenuFilter,
  GridColumnMenuFilterUIProps,
  GridHeaderCellProps,
} from '@progress/kendo-react-grid';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { createPortal } from 'react-dom';
import GridCellBase from 'src/components/GridCellBase';
import { GridColumn } from 'src/components/GridWrapper';
import MessageAttachmentsModal from 'src/components/MessageAttachmentsModal';
import { MESSAGE_COLORS } from 'src/constants/colors';
import useViewMessage from 'src/hooks/useViewMessage';
import MessageSubject from 'src/sections/view_message/view_subject_cell/MessageSubject';
import { Message } from 'src/services/messages_services/useGetMessageApi';

// Custom header for attachments NO sort arrows (prevent double sort arrows)
const AttachmentHeaderNoSort = (props: GridHeaderCellProps) => {
  const { t } = useTranslation();
  return (
    <span className="k-cell-inner" title={t('Attachments')}>
      <span className="k-link w-fit" onClick={props.onClick}>
        <span className="k-column-title k-icon k-i-attachment k-i-clip" />
      </span>
    </span>
  );
};

const AttachmentsCountCell = (props: GridCellProps) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { attachmentsCount, messageId } = props.dataItem;

  const handleAttachmentsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (attachmentsCount > 0) {
      setIsModalVisible(true);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <GridCellBase {...props}>
        <span style={{ display: 'block', textAlign: 'center', width: '100%' }}>
          {attachmentsCount > 0 ? (
            <button
              onClick={handleAttachmentsClick}
              style={{
                color: MESSAGE_COLORS.LINK_BLUE,
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
              title={`${t('View')} ${attachmentsCount} ${t('attachment(s)')}`}
            >
              {attachmentsCount}
            </button>
          ) : (
            attachmentsCount
          )}
        </span>
      </GridCellBase>

      {/* Render Modal using Portal to prevent grid layout issues */}
      {isModalVisible &&
        createPortal(
          <MessageAttachmentsModal
            visible={isModalVisible}
            closeModal={closeModal}
            messageId={messageId}
          />,
          document.body
        )}
    </>
  );
};

// Custom Status Filter Component
const StatusFilterUI = (filterUIProps: GridColumnMenuFilterUIProps) => {
  const { t } = useTranslation();
  const { value, onChange } = filterUIProps.firstFilterProps;
  const { messages } = useViewMessage();

  const statusOptions = useMemo(() => {
    if (!messages || messages.length === 0)
      return [
        { text: t('All'), value: '' },
        { text: t('deleted by all'), value: 'Deleted by all' },
      ];

    const uniqueStatuses = [
      ...new Set(
        messages
          .map((msg: Message) => msg.decodedStatus)
          .filter((status: string) => status && status.trim() !== '')
      ),
    ].sort();

    // Always include "Deleted by all" as an option since we override status display for deleted messages
    const allOptions = [
      { text: t('All'), value: '' },
      { text: t('deleted by all'), value: 'Deleted by all' },
      ...uniqueStatuses.map((status) => ({ text: t(status.toLowerCase()), value: status })),
    ];

    // Remove duplicate "Deleted by all" if it exists in uniqueStatuses
    const filteredOptions = allOptions.filter(
      (option, index, array) => array.findIndex((item) => item.value === option.value) === index
    );

    return filteredOptions;
  }, [messages, t]);

  const handleDropDownChange = (event: DropDownListChangeEvent) => {
    const selectedValue = event.target.value?.value || '';

    // For "Deleted by all" filter, we need to use a custom approach
    if (selectedValue === 'Deleted by all') {
      // Use a special operator to identify this as a deleted filter
      onChange({
        value: 'Deleted by all',
        operator: 'contains', // We'll handle this specially in data processing
        syntheticEvent: event.syntheticEvent,
      });
    } else {
      onChange({
        value: selectedValue,
        operator: selectedValue ? 'eq' : '',
        syntheticEvent: event.syntheticEvent,
      });
    }
  };

  const currentValue = statusOptions.find((option) => option.value === value) || statusOptions[0];

  return (
    <DropDownList
      data={statusOptions}
      textField="text"
      dataItemKey="value"
      value={currentValue}
      onChange={handleDropDownChange}
      style={{ width: '100%' }}
    />
  );
};

// Custom Status Filter Cell
const StatusFilterCell = (columnMenuProps: any) => {
  const { t } = useTranslation();
  const messages = useMemo(
    () => ({
      filterSubmitButton: t('Filter'),
      filterClearButton: t('Clear'),
    }),
    [t]
  );
  return (
    <GridColumnMenuFilter
      {...columnMenuProps}
      expanded={true}
      hideSecondFilter
      filterUI={(filterUIProps: GridColumnMenuFilterUIProps) => (
        <StatusFilterUI {...filterUIProps} />
      )}
      messages={messages}
    />
  );
};

// Custom Priority Filter Component for decodedPriority
const PriorityFilterUI = (filterUIProps: GridColumnMenuFilterUIProps) => {
  const { t } = useTranslation();
  const { value, onChange } = filterUIProps.firstFilterProps;
  const { messages } = useViewMessage();

  // Get unique priority values from messages
  const priorityOptions = useMemo(() => {
    if (!messages || messages.length === 0) return [];

    const uniquePriorities = [
      ...new Set(
        messages
          .map((msg: Message) => msg.decodedPriority)
          .filter(
            (priority: string | undefined): priority is string =>
              priority !== undefined && priority.trim() !== ''
          )
      ),
    ].sort();

    return [
      { text: t('All'), value: '' },
      ...uniquePriorities.map((priority) => ({ text: t(priority), value: priority })),
    ];
  }, [messages, t]);

  const handleDropDownChange = (event: DropDownListChangeEvent) => {
    const selectedValue = event.target.value?.value || '';
    onChange({
      value: selectedValue,
      operator: selectedValue ? 'eq' : '',
      syntheticEvent: event.syntheticEvent,
    });
  };

  const currentValue =
    priorityOptions.find((option) => option.value === value) || priorityOptions[0];

  return (
    <DropDownList
      data={priorityOptions}
      textField="text"
      dataItemKey="value"
      value={currentValue}
      onChange={handleDropDownChange}
      style={{ width: '150px' }}
    />
  );
};

// Custom Priority Filter Cell
const PriorityFilterCell = (columnMenuProps: any) => {
  const { t } = useTranslation();
  const messages = useMemo(
    () => ({
      filterSubmitButton: t('Filter'),
      filterClearButton: t('Clear'),
    }),
    [t]
  );
  return (
    <GridColumnMenuFilter
      {...columnMenuProps}
      expanded={true}
      hideSecondFilter
      filterUI={(filterUIProps: GridColumnMenuFilterUIProps) => (
        <PriorityFilterUI {...filterUIProps} />
      )}
      messages={messages}
    />
  );
};

// Custom Boolean Filter Component for isDeleted
const BooleanFilterUI = (filterUIProps: GridColumnMenuFilterUIProps) => {
  const { t } = useTranslation();
  const { value, onChange } = filterUIProps.firstFilterProps;

  const booleanOptions = [
    { text: t('Show All'), value: '' },
    { text: t('Show Only Deleted'), value: 'true' },
    { text: t('Show All Active'), value: 'false' },
  ];

  const handleDropDownChange = (event: DropDownListChangeEvent) => {
    const selectedValue = event.target.value?.value || '';
    let filterValue = selectedValue;
    let operator = '';

    if (selectedValue === 'true') {
      filterValue = true;
      operator = 'eq';
    } else if (selectedValue === 'false') {
      filterValue = false;
      operator = 'eq';
    }

    onChange({
      value: filterValue,
      operator: operator,
      syntheticEvent: event.syntheticEvent,
    });
  };

  const currentValue =
    booleanOptions.find((option) => {
      if (value === true) return option.value === 'true';
      if (value === false) return option.value === 'false';
      return option.value === '';
    }) || booleanOptions[0];

  return (
    <DropDownList
      data={booleanOptions}
      textField="text"
      dataItemKey="value"
      value={currentValue}
      onChange={handleDropDownChange}
      style={{ width: '150px' }}
    />
  );
};

// Custom Boolean Filter Cell
const BooleanFilterCell = (columnMenuProps: any) => {
  const { t } = useTranslation();
  const messages = useMemo(
    () => ({
      filterSubmitButton: t('Filter'),
      filterClearButton: t('Clear'),
    }),
    [t]
  );
  return (
    <GridColumnMenuFilter
      {...columnMenuProps}
      expanded={true}
      hideSecondFilter
      filterUI={(filterUIProps: GridColumnMenuFilterUIProps) => (
        <BooleanFilterUI {...filterUIProps} />
      )}
      messages={messages}
    />
  );
};

export const useMessageColumnOutbox = () => {
  const { hasEditMessagePermission, t } = useViewMessage();

  //Added to set column widths based on grid size
  const [gridSize, setGridSize] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setGridSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const columnFormat1Size = useMemo(() => {
    // Calculate available width more conservatively to prevent horizontal scroll
    const availableWidth = Math.min(gridSize, 1900);
    const baseWidth = availableWidth >= 1450 ? (availableWidth - 400) * 0.08 : 100;
    return Math.max(baseWidth, 100); // Minimum 100px
  }, [gridSize]);

  const viewMessageColumns: GridColumn<any>[] = useMemo(
    () => [
      ...(hasEditMessagePermission
        ? ([
            {
              field: 'selected',
              title: '',
              menu: false,
              // Note: selectedRows and selectedDelRows are calculated in parent (ViewMessageTable)
            },
          ] as const)
        : []),

      {
        field: 'decodedPriority',
        title: t('Priority'),
        filter: 'text',
        filterable: true,
        sortable: true,
        width: 60,
        className: 'text-left',
        columnMenu: PriorityFilterCell,
        component: (props: GridCellProps) => {
          const priority = props.dataItem.decodedPriority;
          const getPriorityColor = (priority: string) => {
            const priorityLower = priority?.toLowerCase() || '';
            if (priorityLower === 'urgent') return 'firebrick';
            if (priorityLower === 'high') return '#e66b00';
            if (priorityLower === 'low') return '#1e90ff';
            return 'black'; // Normal or default
          };
          return (
            <GridCellBase {...props}>
              <span
                style={{
                  display: 'block',
                  textAlign: 'left',
                  width: '100%',
                  color: getPriorityColor(priority),
                }}
              >
                {t(priority)}
              </span>
            </GridCellBase>
          );
        },
      },

      /*
      Attachments Count Column - do not show for now (January 2026). Will be in Phase Two.
       {
        field: 'attachmentsCount',
        //title: '📎',
        filterable: false,
        menu: false,
        sortable: true,
        width: 30,
        resizable: false,
        className: 'text-center attachments-header',
        headerCell: AttachmentHeaderNoSort,
        component: AttachmentsCountCell,
      },
      */

      {
        field: 'messageId',
        title: t('Message ID'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size - 40,
        show: false,
      },

      /* -----HIDE Deleted (Resp) column --- 
      {
        field: 'isDeleted',
        title: t('Deleted (Resp)'),
        filter: 'boolean',
        sortable: true,
        filterable: true,
        width: 100,
        className: 'text-center',
        hidden: true,
        columnMenu: BooleanFilterCell,
        component: (props: GridCellProps) => (
          <GridCellBase {...props}>
            <span
              style={{
                display: 'block',
                textAlign: 'center',
                width: '100%',
                color: props.dataItem.isDeleted ? 'firebrick' : 'inherit',
                backgroundColor: props.dataItem.isDeleted ? MESSAGE_COLORS.DELETED_STATUS_BG : MESSAGE_COLORS.TRANSPARENT,
                padding: props.dataItem.isDeleted ? '2px 4px' : '0',
                borderRadius: props.dataItem.isDeleted ? '3px' : '0',
              }}
            >
              {props.dataItem.isDeleted ? 'Deleted by all' : 'Show Only Deleted'}
            </span>
          </GridCellBase>
        ),
      },
      */

      {
        field: 'decodedStatus',
        title: t('Status'),
        filter: 'text',
        filterable: true,
        columnMenu: StatusFilterCell,
        sortable: true,
        width: 80,
        component: (props: GridCellProps) => {
          const value = props.dataItem.decodedStatus;

          let cellStyle: React.CSSProperties | undefined;
          let textStyle: React.CSSProperties = {
            fontStyle: 'italic',
            display: 'block',
            width: '100%',
          };

          // Apply status-based styling
          {
            // Original status styling logic
            switch (value.replace(/\s+/g, ' ').trim().toLowerCase()) {
              case 'new':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_NEW };
                break;
              case 'read':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_READ };
                break;
              case 'viewed':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_VIEWED };
                break;
              case 'replied':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_REPLIED };
                break;
              case 'deleted by recipient':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_DELBYRECIP };
                textStyle = { ...textStyle, color: 'black' };
                break;
              case 'deleted':
              case 'deleted by all':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_DEL };
                textStyle = { ...textStyle, color: 'black' };
                break;
              case 'deleted by sender':
                cellStyle = { backgroundColor: MESSAGE_COLORS.STATUS_DELBYSENDER };
                textStyle = { ...textStyle, color: 'black' };
                break;
              default:
                cellStyle = { backgroundColor: 'white' };
                break;
            }
          }

          return (
            <GridCellBase {...props} style={cellStyle}>
              <span style={textStyle}>{t(value.toLowerCase())}</span>
            </GridCellBase>
          );
        },
      },

      /*----- Hide column for raw Status (numeric) ----- 
      {
        field: 'status',
        title: t('Status (raw)'),
        filter: 'numeric',
        sortable: true,
        filterable: true,
        width: 85,
        className: 'text-center',
        hidden: false,
      },
      */

      /* ------ HIDE Deleted (Cast) column ---- 
      {
        field: 'isDeletedMsgCast',
        title: t('Deleted (Cast)'),
        filter: 'boolean',
        sortable: true,
        filterable: true,
        width: 100,
        className: 'text-center',
        hidden: true,
        component: (props: GridCellProps) => (
          <GridCellBase {...props}>
            <span
              style={{
                display: 'block',
                textAlign: 'center',
                width: '100%',
                color: props.dataItem.isDeletedMsgCast ? 'crimson' : 'inherit',
              }}
            >
              {props.dataItem.isDeletedMsgCast ? 'Yes' : 'No'}
            </span>
          </GridCellBase>
        ),
      },
      */

      {
        field: 'recipientCompanyName',
        title: t('To Company'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 30,
      },

      {
        field: 'recipient',
        title: t('Recipient'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 70,
      },

      /* ------------------------------------------------------
        HIDE Recipient ClientId column 
        
        { field: 'clientId', title: t('Recipient ClientId'),
          filter: 'text', sortable: true, filterable: true, width: 150,
        },
      -------------------------------------------------------*/

      {
        field: 'subject',
        title: t('Subject'),
        component: MessageSubject,
        dataType: 'string',
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 60,
        //disableCellTooltip: true,
      },

      {
        field: 'message',
        title: t('Message'),
        filter: 'text',
        sortable: true,
        filterable: true,
        width: columnFormat1Size + 140,
        className: 'message-column-cell',
        component: (props: GridCellProps) => (
          <GridCellBase {...props} className="message-column-cell">
            <span className="message-cell-hover-expand">{props.dataItem.message}</span>
          </GridCellBase>
        ),
      },

      {
        field: 'creationDate',
        title: t('Date/Time Sent'),
        sortable: true,
        filterable: true,
        filter: 'date',
        width: columnFormat1Size,
        component: (props: GridCellProps) => {
          // Sort on creationDate (full datetime), but filter on creationDateOnly (date only)
          const raw = props.dataItem.creationDate;
          const date = raw instanceof Date ? raw : raw ? new Date(raw) : null;
          // Only show the formatted value as the tooltip (no day-of-week, no full Date string)
          const formatted =
            date && !isNaN(date.getTime()) ? format(date, 'MM/dd/yyyy hh:mm a') : '';
          return (
            <GridCellBase {...props} title={formatted}>
              <span>{formatted}</span>
            </GridCellBase>
          );
        },
      },
    ],
    [hasEditMessagePermission, t, columnFormat1Size]
  );
  return viewMessageColumns;
};
